import React, { useState, useEffect } from 'react';

// Icon components
const CalendarIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const ClockIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const UsersIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
    </svg>
);

const EventsTab = ({ playerId }) => {
    const [events, setEvents] = useState({
        past: [],
        upcoming: [],
        live: []
    });
    const [teamInfo, setTeamInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch events
                const eventsResponse = await fetch(`https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/event/get-all-events-by-team-member-id?memberId=${playerId}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (!eventsResponse.ok) throw new Error('Failed to fetch events');
                const eventsData = await eventsResponse.json();
                setEvents(eventsData);

                // Fetch team information
                const teamResponse = await fetch(`https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/team/show-team-by-player-id?playerId=${playerId}`, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (teamResponse.ok) {
                    const teamData = await teamResponse.json();
                    setTeamInfo(teamData.team);
                }
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [playerId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading events...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                    <div className="text-red-400">
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Error loading events</h3>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const renderEventCard = (event, status) => {
        const statusConfig = {
            live: {
                borderColor: 'border-green-500',
                bgColor: 'bg-green-50',
                textColor: 'text-green-700',
                icon: '🔴',
                label: 'Live Now'
            },
            upcoming: {
                borderColor: 'border-blue-500',
                bgColor: 'bg-blue-50',
                textColor: 'text-blue-700',
                icon: '📅',
                label: 'Upcoming'
            },
            past: {
                borderColor: 'border-gray-400',
                bgColor: 'bg-gray-50',
                textColor: 'text-gray-600',
                icon: '✅',
                label: 'Completed'
            }
        };

        const config = statusConfig[status] || statusConfig.upcoming;

        return (
            <div key={event.id} className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border-l-4 ${config.borderColor}`}>
                <div className="p-6">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                                {teamInfo?.logo && (
                                    <img 
                                        src={teamInfo.logo} 
                                        alt={teamInfo.name}
                                        className="h-10 w-10 rounded-full object-cover border-2 border-gray-200"
                                    />
                                )}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                                    {teamInfo && (
                                        <p className="text-sm text-gray-500">{teamInfo.name} • {teamInfo.sport}</p>
                                    )}
                                </div>
                            </div>
                            
                            {event.description && (
                                <p className="text-gray-600 text-sm mb-3">{event.description}</p>
                            )}
                            
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <div className="flex items-center space-x-1">
                                    <CalendarIcon className="h-4 w-4" />
                                    <span>{new Date(event.startDate).toLocaleDateString()}</span>
                                </div>
                                {event.startDate && (
                                    <div className="flex items-center space-x-1">
                                        <ClockIcon className="h-4 w-4" />
                                        <span>{new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                )}
                                <div className="flex items-center space-x-1">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        {event.eventType || 'Event'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${config.bgColor}`}>
                            <span className="text-sm">{config.icon}</span>
                            <span className={`text-xs font-medium ${config.textColor}`}>{config.label}</span>
                        </div>
                    </div>
                    
                    {event.teams && event.teams.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center space-x-1 text-sm text-gray-500">
                                <UsersIcon className="h-4 w-4" />
                                <span>Teams: {event.teams.map(team => team.name).join(', ')}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const totalEvents = (events.live?.length || 0) + (events.upcoming?.length || 0) + (events.past?.length || 0);

    return (
        <div className="space-y-8">
            {/* Header with team info */}
            {teamInfo && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                    <div className="flex items-center space-x-4">
                        <img 
                            src={teamInfo.logo} 
                            alt={teamInfo.name}
                            className="h-16 w-16 rounded-full object-cover border-4 border-white shadow-md"
                        />
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{teamInfo.name}</h1>
                            <p className="text-gray-600">{teamInfo.sport} • {teamInfo.type}</p>
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                                <span className="flex items-center space-x-1">
                                    <CalendarIcon className="h-4 w-4" />
                                    <span>{totalEvents} total events</span>
                                </span>
                                {teamInfo.organizationDetails && (
                                    <span>{teamInfo.organizationDetails.name}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Live Events */}
            <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span>Live Events</span>
                    {events.live?.length > 0 && (
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                            {events.live.length}
                        </span>
                    )}
                </h2>
                <div className="space-y-4">
                    {events.live && events.live.length > 0 ? (
                        events.live.map(event => renderEventCard(event, 'live'))
                    ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 text-4xl mb-2">📅</div>
                            <p className="text-gray-500 font-medium">No live events</p>
                            <p className="text-gray-400 text-sm">Events currently in progress will appear here</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Upcoming Events */}
            <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <CalendarIcon className="h-5 w-5 text-blue-500" />
                    <span>Upcoming Events</span>
                    {events.upcoming?.length > 0 && (
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                            {events.upcoming.length}
                        </span>
                    )}
                </h2>
                <div className="space-y-4">
                    {events.upcoming && events.upcoming.length > 0 ? (
                        events.upcoming.map(event => renderEventCard(event, 'upcoming'))
                    ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 text-4xl mb-2">⏰</div>
                            <p className="text-gray-500 font-medium">No upcoming events</p>
                            <p className="text-gray-400 text-sm">Future events will be displayed here</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Past Events */}
            <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-gray-500" />
                    <span>Past Events</span>
                    {events.past?.length > 0 && (
                        <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
                            {events.past.length}
                        </span>
                    )}
                </h2>
                <div className="space-y-4">
                    {events.past && events.past.length > 0 ? (
                        events.past.map(event => renderEventCard(event, 'past'))
                    ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <div className="text-gray-400 text-4xl mb-2">📚</div>
                            <p className="text-gray-500 font-medium">No past events</p>
                            <p className="text-gray-400 text-sm">Completed events will be shown here</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventsTab; 